import type { AgentMemoryObservation, PolicyCondition, PolicyV2 } from '@schemas/api';

type JsonRecord = Record<string, unknown>;

export interface MemoryPresentation {
  category: string;
  title: string;
  summary: string;
  appliesTo: string;
  source: string;
  technicalDetails: Array<{ label: string; value: string }>;
}

export interface PolicyPresentation {
  category: string;
  title: string;
  summary: string;
  scope: string;
  conditions: string[];
  action: string;
}

export interface PolicyActionGroup {
  key: string;
  category: string;
  action: string;
  rules: Array<{ policy: PolicyV2; presentation: PolicyPresentation }>;
}

const parseJson = (value?: string | null): unknown => {
  if (!value) return undefined;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const asRecord = (value: unknown): JsonRecord =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as JsonRecord : {};

const asString = (value: unknown): string =>
  typeof value === 'string' ? value : value == null ? '' : String(value);

export const humanize = (value: string) =>
  value
    .replace(/[:_]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const opaqueReferencePattern = /(?:\bezref_[A-Za-z0-9_-]+\b|\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b)/gi;

const isOpaqueReference = (value: string) => {
  opaqueReferencePattern.lastIndex = 0;
  return opaqueReferencePattern.test(value);
};

export const redactOpaqueReferences = (value: string, replacement = 'this record') =>
  value.replace(opaqueReferencePattern, replacement);

const humanizeSafely = (value: string) => humanize(redactOpaqueReferences(value));

const displayValue = (value: unknown, fallback: string): string => {
  if (Array.isArray(value)) {
    const values: string[] = value.map((item) => displayValue(item, fallback)).filter(Boolean);
    return values.length ? values.join(', ') : fallback;
  }
  if (value && typeof value === 'object') {
    const record = value as JsonRecord;
    for (const key of ['label', 'name', 'display_name', 'code', 'number', 'value']) {
      if (record[key] != null) return displayValue(record[key], fallback);
    }
    return fallback;
  }
  const text = asString(value).trim();
  return !text || isOpaqueReference(text) ? fallback : redactOpaqueReferences(text, fallback);
};

const resolvedLabel = (memory: AgentMemoryObservation, value: unknown, fallback: string) => {
  const raw = asString(value);
  return memory.reference_labels?.[raw] || displayValue(raw, fallback);
};

const findStringField = (value: unknown, field: string): string => {
  if (Array.isArray(value)) {
    return value.map((item) => findStringField(item, field)).find(Boolean) || '';
  }
  const record = asRecord(value);
  if (record[field] != null) return asString(record[field]);
  return Object.values(record).map((item) => findStringField(item, field)).find(Boolean) || '';
};

const valueRecord = (memory: AgentMemoryObservation, content: JsonRecord) => {
  const structuredValue = parseJson(memory.rule_value);
  if (structuredValue !== undefined) return asRecord(structuredValue);
  return asRecord(parseJson(asString(content.value)));
};

const ruleParts = (memory: AgentMemoryObservation) => {
  const content = asRecord(parseJson(memory.content));
  return {
    content,
    ruleKey: memory.rule_key || asString(content.key),
    ruleType: memory.rule_type || asString(content.rule_type),
    ruleValue: memory.rule_value || asString(content.value),
    values: valueRecord(memory, content),
  };
};

const sourceLabel = (source: string) => {
  const labels: Record<string, string> = {
    ap_posting_review: 'AP posting review',
    document_review: 'document review',
    manual: 'manual instruction',
    manual_update: 'manual update',
    chat: 'chat instruction',
    workflow: 'automated matching',
  };
  return labels[source] || humanize(source);
};

const sourceDescription = (memory: AgentMemoryObservation, content: JsonRecord, values: JsonRecord) => {
  const documentId = findStringField([content, values], 'document_id');
  if (documentId && memory.reference_labels?.[documentId]) {
    return memory.reference_labels[documentId];
  }
  if (memory.source_ref && memory.reference_labels?.[memory.source_ref]) {
    return memory.reference_labels[memory.source_ref];
  }
  const documentLabel = Object.values(memory.reference_labels || {}).find((label) =>
    /^(Invoice|Receipt|Purchase order) /.test(label));
  if (documentLabel) return documentLabel;
  return sourceLabel(memory.source);
};

const scopeLabel = (memory: AgentMemoryObservation, ruleType: string) => {
  if (ruleType === 'vendor_specific') {
    return (memory.source_ref && memory.reference_labels?.[memory.source_ref]) || 'This vendor';
  }
  if (ruleType === 'direct_override') return 'Direct override';
  if (ruleType === 'tenant_default' || memory.proposed_scope === 'tenant') return 'All tenant activity';
  return 'Your activity';
};

export const presentMemory = (memory: AgentMemoryObservation): MemoryPresentation => {
  const { content, ruleKey, ruleType, ruleValue, values } = ruleParts(memory);
  const documentId = findStringField([content, values], 'document_id');
  const vendorId = findStringField([content, values], 'vendor_id');
  const appliesTo = scopeLabel(memory, ruleType);
  const source = sourceDescription(memory, content, values);
  const technicalDetails = [
    ...(ruleKey ? [{ label: 'Rule', value: humanizeSafely(ruleKey) }] : []),
    ...(memory.source_ref ? [{
      label: 'Related entity',
      value: memory.reference_labels?.[memory.source_ref] || 'Related record',
    }] : []),
    ...(documentId
      ? [{
        label: 'Document',
        value: memory.reference_labels?.[documentId] || 'Related document',
      }]
      : []),
    ...(vendorId
      ? [{
        label: 'Vendor',
        value: memory.reference_labels?.[vendorId] || 'Related vendor',
      }]
      : []),
  ];

  if (memory.observation_type === 'episodic_preference' || !ruleKey) {
    return {
      category: 'Instruction',
      title: memory.proposed_scope === 'tenant' ? 'Team instruction' : 'Personal instruction',
      summary: redactOpaqueReferences(memory.content),
      appliesTo,
      source,
      technicalDetails,
    };
  }

  if (ruleKey.startsWith('ap_line_item:')) {
    const line = humanizeSafely(ruleKey.slice('ap_line_item:'.length));
    const item = resolvedLabel(memory, values.erp_item_ref, 'the selected ERP item');
    return {
      category: 'Line coding',
      title: `Use “${item}” for matching invoice lines`,
      summary: `Apply this item when a line resembles “${line}”.`,
      appliesTo,
      source,
      technicalDetails,
    };
  }

  if (ruleKey.startsWith('ap_line_account:')) {
    const line = humanizeSafely(ruleKey.slice('ap_line_account:'.length));
    const account = resolvedLabel(memory, values.account_ref, 'the selected account');
    return {
      category: 'Account coding',
      title: `Code matching lines to “${account}”`,
      summary: `Apply this account when a line resembles “${line}”.`,
      appliesTo,
      source,
      technicalDetails,
    };
  }

  if (ruleKey.startsWith('vendor_alias:')) {
    const alias = humanizeSafely(ruleKey.slice('vendor_alias:'.length));
    const vendor = resolvedLabel(memory, ruleValue, 'the linked vendor');
    return {
      category: 'Vendor recognition',
      title: `Recognize “${alias}” as “${vendor}”`,
      summary: 'Use this vendor link when the extracted vendor name matches this alias.',
      appliesTo,
      source,
      technicalDetails,
    };
  }

  if (ruleKey.startsWith('po_line_alias:')) {
    const alias = humanizeSafely(ruleKey.slice('po_line_alias:'.length));
    const poLine = resolvedLabel(memory, values.po_line_id, 'the selected PO line');
    return {
      category: 'PO line matching',
      title: `Match “${alias}” to ${poLine}`,
      summary: asString(values.line_description) || 'Use this alias when matching invoice and PO lines.',
      appliesTo,
      source,
      technicalDetails,
    };
  }

  if (ruleKey.startsWith('po_match_policy:')) {
    const poNumber = displayValue(
      asString(values.po_number) || ruleKey.slice('po_match_policy:'.length),
      'the selected purchase order',
    );
    const decision = asString(values.decision);
    const rejected = decision.includes('reject');
    return {
      category: 'PO matching',
      title: rejected ? `Avoid suggesting PO ${poNumber}` : `Prefer PO ${poNumber}`,
      summary: rejected
        ? 'A reviewer rejected this match pattern.'
        : 'A reviewer accepted this match pattern.',
      appliesTo,
      source,
      technicalDetails,
    };
  }

  const resolvedScalar = memory.reference_labels?.[ruleValue];
  if (ruleType === 'vendor_specific' && resolvedScalar) {
    const trigger = ruleKey.includes(':') || ruleKey.includes('_') ? humanizeSafely(ruleKey) : redactOpaqueReferences(ruleKey);
    return {
      category: 'Learned mapping',
      title: `Use “${resolvedScalar}” when “${trigger}” appears`,
      summary: 'This association was captured from a reviewed document.',
      appliesTo,
      source,
      technicalDetails,
    };
  }

  const scalarValue = resolvedLabel(memory, ruleValue, 'the captured value');
  const knownRules: Record<string, { category: string; title: string; summary: string }> = {
    ap_header_account: {
      category: 'Account coding',
      title: `Use “${scalarValue}” as the header account`,
      summary: 'Apply this account when creating an AP posting.',
    },
    ap_discount_account: {
      category: 'Discount coding',
      title: `Use “${scalarValue}” for discounts`,
      summary: 'Apply this account when posting an early-payment discount.',
    },
    ap_tax_codes: {
      category: 'Tax coding',
      title: 'Reuse the selected tax codes',
      summary: Array.isArray(parseJson(ruleValue))
        ? (parseJson(ruleValue) as unknown[]).map(asString).join(', ')
        : scalarValue,
    },
    po_number_pattern: {
      category: 'PO matching',
      title: `Recognize PO number “${scalarValue}”`,
      summary: 'Use this pattern when matching documents to purchase orders.',
    },
    vendor_payment_terms: {
      category: 'Vendor default',
      title: `Use payment terms “${scalarValue}”`,
      summary: 'Apply these payment terms for this vendor.',
    },
    vendor_currency: {
      category: 'Vendor default',
      title: `Use currency ${scalarValue}`,
      summary: 'Apply this currency for this vendor.',
    },
  };
  const known = knownRules[ruleKey];
  if (known) return { ...known, appliesTo, source, technicalDetails };

  return {
    category: 'Captured policy',
    title: humanizeSafely(ruleKey),
    summary: `Use “${scalarValue}” when this rule applies.`,
    appliesTo,
    source,
    technicalDetails,
  };
};

const dimensionLabel = (dimension: string) => {
  const labels: Record<string, string> = {
    vendor: 'Vendor',
    parent_vendor: 'Parent vendor',
    document_type: 'Document type',
    line_signature: 'Line description',
    expense_type: 'Expense type',
    org_unit: 'Business unit',
    location: 'Location',
    department: 'Department',
    class: 'Class',
    cost_center: 'Cost center',
    project: 'Project',
    currency: 'Currency',
    ocr_phrase: 'Document text',
    erp_item_type: 'Item type',
    erp_account_type: 'Account type',
  };
  return labels[dimension] || humanize(dimension);
};

const operatorLabel = (operator: string) => ({
  equals: 'is',
  not_equals: 'is not',
  in: 'is one of',
  not_in: 'is not one of',
  contains: 'contains',
  starts_with: 'starts with',
  ends_with: 'ends with',
  regex: 'matches',
}[operator] || humanize(operator).toLowerCase());

const conditionFallback = (dimension: string) => `a selected ${dimensionLabel(dimension).toLowerCase()}`;

const presentCondition = (condition: PolicyCondition) => {
  const value = condition.value_label || displayValue(
    condition.value_ref ?? condition.value,
    conditionFallback(condition.dimension),
  );
  return `${dimensionLabel(condition.dimension)} ${operatorLabel(condition.operator)} ${value}`;
};

const scopeDescription = (sourceType: string, sourceLabel?: string | null) => {
  if (sourceLabel) return `For ${sourceLabel}`;
  return {
    tenant: 'All tenant activity',
    vendor: 'A vendor-specific rule',
    parent_vendor: 'A parent-vendor rule',
    org_unit: 'A business-unit rule',
  }[sourceType] || `${humanize(sourceType || 'General')} rule`;
};

const policyCategory = (family: string) => ({
  'coding.gl_account': 'Account coding',
  'coding.erp_item': 'Item coding',
}[family] || humanize(family || 'Coding policy'));

export const presentPolicy = (policy: PolicyV2): PolicyPresentation => {
  const isAccountPolicy = policy.family === 'coding.gl_account';
  const targetKind = isAccountPolicy ? 'GL account' : 'ERP item';
  const target = policy.action.target_label || displayValue(
    policy.action.target_ref || policy.action.value,
    `the selected ${targetKind}`,
  );
  const action = isAccountPolicy
    ? `Code matching lines to ${target}`
    : `Use ${target} for matching lines`;
  const conditions = policy.conditions.map(presentCondition);
  return {
    category: policyCategory(policy.family),
    title: action,
    summary: conditions.length
      ? `Apply when ${conditions.join(' and ')}.`
      : 'Apply this rule whenever a matching line is reviewed.',
    scope: scopeDescription(policy.source_type, policy.source_label),
    conditions,
    action,
  };
};

/** Group rules by the outcome they produce, so their different conditions are comparable. */
export const groupPoliciesByAction = (policies: PolicyV2[]): PolicyActionGroup[] => {
  const groups = new Map<string, PolicyActionGroup>();

  for (const policy of policies) {
    const presentation = presentPolicy(policy);
    const target = policy.action.target_ref
      || policy.action.target_label
      || JSON.stringify(policy.action.value ?? '');
    const key = [policy.family, policy.action.action_type || '', target].join(':');
    const existing = groups.get(key);
    if (existing) {
      existing.rules.push({ policy, presentation });
    } else {
      groups.set(key, {
        key,
        category: presentation.category,
        action: presentation.action,
        rules: [{ policy, presentation }],
      });
    }
  }

  return [...groups.values()].sort((left, right) => right.rules.length - left.rules.length);
};
